import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Moon, Shield, Sun } from "lucide-react";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";

import { AuthContext } from "../Context/AuthContext";
import { ToastStack, useToastStack } from "../../components/ui/ToastStack";
import {
  IamAuditTab,
  IamDashboardTab,
  IamPoliciesTab,
  IamSessionsTab,
  IamWorkflowsTab,
} from "./IamTabs";
import IamUsersStudio from "./IamUsersStudio";
import IamRolesStudio from "./IamRolesStudio";
import IamPermissionTemplatesStudio from "./IamPermissionTemplatesStudio";
import IamAccessScopeStudio from "./IamAccessScopeStudio";
import {
  bulkIamUsers,
  createIamRole,
  createWorkflow,
  fetchAuditLogs,
  fetchIamCatalog,
  fetchIamDashboard,
  fetchIamRoles,
  fetchIamUsers,
  fetchPolicies,
  fetchSessions,
  fetchWorkflows,
  revokeSession,
  seedIam,
  resetIamUserPassword,
  saveAccessScope,
  setUserAccess,
  updateIamRole,
} from "./iamApi";
import "./iam.css";

const TABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "users", label: "Users" },
  { id: "roles", label: "Roles" },
  { id: "templates", label: "Permission Templates" },
  { id: "access", label: "Access Scope" },
  { id: "workflows", label: "Approval Workflows" },
  { id: "policies", label: "Access Policies" },
  { id: "sessions", label: "Session Management" },
  { id: "audit", label: "Audit Logs" },
];

function apiError(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  return fallback;
}

export default function IamAccessManagement() {
  const { toasts, addToast, removeToast } = useToastStack();
  const { user: me, role: authRole } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = useMemo(() => {
    const r = (me?.role || me?.user?.role || authRole || "").toLowerCase();
    return r === "admin" || Boolean(me?.user?.is_system_admin || me?.is_system_admin);
  }, [me, authRole]);

  const [dark, setDark] = useState(() => localStorage.getItem("iam_dark") === "1");
  const [tab, setTab] = useState(() => {
    const fromUrl = searchParams.get("tab");
    return TABS.some((t) => t.id === fromUrl) ? fromUrl : "dashboard";
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [dashboard, setDashboard] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [scopeUserId, setScopeUserId] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const securitySettings = null;

  useEffect(() => {
    localStorage.setItem("iam_dark", dark ? "1" : "0");
  }, [dark]);

  useEffect(() => {
    const fromUrl = searchParams.get("tab");
    if (fromUrl && TABS.some((t) => t.id === fromUrl) && fromUrl !== tab) {
      setTab(fromUrl);
    }
  }, [searchParams, tab]);

  const selectTab = useCallback(
    (id) => {
      setTab(id);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id === "dashboard") next.delete("tab");
          else next.set("tab", id);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const loadTab = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "dashboard") setDashboard(await fetchIamDashboard());
      if (tab === "users") {
        const [people, roleRows] = await Promise.all([fetchIamUsers({}), fetchIamRoles()]);
        setUsers(people);
        setRoles(roleRows);
      }
      if (tab === "roles" || tab === "templates") {
        const [r, c] = await Promise.all([fetchIamRoles(), fetchIamCatalog()]);
        setRoles(r);
        setCatalog(c);
      }
      if (tab === "access") {
        setLoading(false);
        return;
      }
      if (tab === "workflows") setWorkflows(await fetchWorkflows());
      if (tab === "policies") setPolicies(await fetchPolicies());
      if (tab === "sessions") setSessions(await fetchSessions());
      if (tab === "audit") setAuditLogs(await fetchAuditLogs());
    } catch (err) {
      addToast(apiError(err, "Failed to load IAM data"), "error");
    } finally {
      setLoading(false);
    }
  }, [tab, addToast]);

  useEffect(() => {
    loadTab();
  }, [loadTab]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const r = await seedIam();
      addToast(`Seeded ${r.created?.length || 0} roles`, "success");
      await loadTab();
    } catch (err) {
      addToast(apiError(err, "Seed failed"), "error");
    } finally {
      setSeeding(false);
    }
  };

  const handleSaveRole = async (roleId, form) => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        role_category: form.role_category,
        permission_matrix: form.permission_matrix || {},
        field_security: form.field_security || [],
      };
      if (form.status) payload.status = form.status;
      if (roleId) await updateIamRole(roleId, payload);
      else await createIamRole(payload);
      addToast("Role saved", "success");
      await loadTab();
    } catch (err) {
      addToast(apiError(err, "Save failed"), "error");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleAssignRoles = async (payload) => {
    setSaving(true);
    try {
      const result = await setUserAccess(payload);
      addToast(
        `Roles updated: ${(result.roles || []).join(", ")}${result.login_created ? " (login created)" : ""}`,
        "success"
      );
      if (tab === "users") await loadTab();
      return result;
    } catch (err) {
      addToast(apiError(err, "Failed to assign roles"), "error");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className={`iam-page ${dark ? "iam-dark" : ""}`}>
        <div className="iam-inner">
          <div className="iam-panel" style={{ marginTop: "2rem", textAlign: "center", padding: "3rem" }}>
            <Shield size={40} style={{ color: "var(--iam-primary)", margin: "0 auto 1rem" }} />
            <h2 style={{ fontSize: "1.25rem", fontWeight: 700 }}>Admin access required</h2>
            <p style={{ color: "var(--iam-muted)", marginTop: "0.5rem" }}>
              Identity &amp; Access Management is restricted to organisation administrators.
            </p>
            <Link to="/settings" className="iam-btn primary" style={{ marginTop: "1.25rem", display: "inline-flex" }}>
              <ArrowLeft size={14} /> Back to Settings
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`iam-page ${dark ? "iam-dark" : ""}`}>
      <ToastStack toasts={toasts} onRemove={removeToast} />
      <div className="iam-inner">
        {/* The Add User page shows only its own form (breadcrumb + "Back to Users"). */}
        {!(tab === "users" && searchParams.get("view") === "add") && (
        <nav className="iam-tabs" aria-label="IAM sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`iam-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => selectTab(t.id)}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            className="iam-tab iam-theme"
            onClick={() => setDark((d) => !d)}
            aria-label="Toggle dark mode"
          >
            {dark ? <Sun size={14} /> : <Moon size={14} />}
            {dark ? "Light" : "Dark"}
          </button>
        </nav>
        )}

        {loading && !["dashboard", "users", "roles", "templates", "access"].includes(tab) ? (
          <div className="iam-loading"><BrixigoPageLoader compact message="Loading…" fullPage={false} /></div>
        ) : (
          <>
            {tab === "dashboard" && (
              <IamDashboardTab dashboard={dashboard} loading={loading} onSeed={handleSeed} seeding={seeding} />
            )}
            {tab === "users" && (
              <IamUsersStudio
                users={users}
                roles={roles}
                loading={loading}
                saving={saving}
                onCreateUser={handleAssignRoles}
                onChangeRole={handleAssignRoles}
                onSetStatus={async (userId, action) => {
                  try {
                    await bulkIamUsers({ user_ids: [userId], action });
                    addToast(action === "activate" ? "User activated" : "User deactivated", "success");
                    await loadTab();
                  } catch (err) {
                    addToast(apiError(err, "Could not update user status"), "error");
                    throw err;
                  }
                }}
                onResetPassword={async (user) => {
                  try {
                    await resetIamUserPassword(user.user_id);
                    addToast("Password reset email sent", "success");
                  } catch (err) {
                    addToast(apiError(err, "Could not send reset email"), "error");
                    throw err;
                  }
                }}
                onSaveScope={async (userId, payload) => {
                  await saveAccessScope(userId, payload);
                }}
                onViewAccess={(userId) => {
                  setScopeUserId(userId);
                  selectTab("access");
                }}
              />
            )}
            {tab === "roles" && (
              <IamRolesStudio
                roles={roles}
                catalog={catalog}
                loading={loading}
                saving={saving}
                onSaveRole={handleSaveRole}
              />
            )}
            {tab === "templates" && (
              <IamPermissionTemplatesStudio
                roles={roles}
                catalog={catalog}
                loading={loading}
                onOpenRoles={() => selectTab("roles")}
              />
            )}
            {tab === "access" && (
              <IamAccessScopeStudio
                initialUserId={scopeUserId}
                onConsumedInitial={() => setScopeUserId(null)}
                onNotify={addToast}
              />
            )}
            {tab === "workflows" && (
              <IamWorkflowsTab
                workflows={workflows}
                onCreate={async (p) => {
                  try {
                    await createWorkflow(p);
                    addToast("Workflow created", "success");
                    await loadTab();
                  } catch (err) {
                    addToast(apiError(err, "Failed"), "error");
                  }
                }}
              />
            )}
            {tab === "policies" && (
              <IamPoliciesTab policies={policies} securitySettings={securitySettings} />
            )}
            {tab === "sessions" && (
              <IamSessionsTab
                sessions={sessions}
                onRevoke={async (id) => {
                  try {
                    await revokeSession(id);
                    addToast("Session revoked", "success");
                    await loadTab();
                  } catch (err) {
                    addToast(apiError(err, "Revoke failed"), "error");
                  }
                }}
              />
            )}
            {tab === "audit" && <IamAuditTab logs={auditLogs} />}
          </>
        )}
      </div>

    </div>
  );
}
